import type { AnyRouter } from '@tanstack/react-router'
// Type-only import — `import type` is stripped at compile time, so this
// does NOT pull the ~640 KB runtime chunk. The actual module loads via
// the dynamic `import('gleap')` inside `initGleap()` only after the
// bootstrap component decides to initialize (post white-label resolve,
// never on external-signer routes, never for support-chat-disabled
// accounts).
import type Gleap from 'gleap'

import { LOCALE_TO_BCP47 } from '@/lib/intl/locale-bcp47'
import { logger } from '@/lib/logger'
import { useContextStore } from '@/stores/context'
import type { Locale } from '@/stores/language'
import { useLanguageStore } from '@/stores/language'
import { useThemeStore } from '@/stores/theme'
import { useWhiteLabelStore } from '@/stores/white-label'

type GleapModule = typeof Gleap

// Must match Tailwind v4's --breakpoint-md (48rem) and use-mobile hook so the
// JS-driven Gleap visibility agrees with CSS-driven layout at every viewport.
const DESKTOP_MEDIA_QUERY = '(min-width: 48rem)'

const EXTERNAL_SIGNER_ROUTE_PATTERNS: ReadonlyArray<RegExp> = [
  /^\/sign-mark(\/|$)/,
  /^\/selfie(\/|$)/,
  /^\/review(\/|$)/,
  /^\/unsubscribe(\/|$)/,
  /^\/view(\/|$)/,
]

// Routes where the bubble still renders (so users can ask for help mid-flow)
// but the footer spacer must NOT — these pages own their own layout (fixed
// bottom action bars, fullscreen wizards) and 80px of trailing padding throws
// off their math.
const SPACER_DISABLED_ROUTE_PATTERNS: ReadonlyArray<RegExp> = [
  /^\/envelope$/,
  /^\/envelope\/[^/]+$/,
  /^\/template$/,
  /^\/template\/[^/]+$/,
]

export const shouldRenderGleapSpacer = (pathname: string): boolean =>
  !SPACER_DISABLED_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))

// `gleapInstance` is the loaded SDK reference; null until `initGleap()`
// resolves successfully. Every API call in this file (and the analytics
// provider) goes through it instead of a top-level import so the chunk
// stays unloaded until it's actually needed.
let gleapInstance: GleapModule | null = null
let gleapInitialized = false
let gleapForceHidden = false
let gleapBubbleVisible = false
let gleapSuppressions = 0
let reevaluateGleapVisibility: (() => void) | null = null

type VisibilityListener = (visible: boolean) => void
const visibilityListeners = new Set<VisibilityListener>()

const setGleapBubbleVisible = (visible: boolean): void => {
  if (gleapBubbleVisible === visible) return
  gleapBubbleVisible = visible
  visibilityListeners.forEach((cb) => cb(visible))
}

export const getIsGleapVisible = (): boolean => gleapBubbleVisible

export const subscribeToGleapVisibility = (
  cb: VisibilityListener,
): (() => void) => {
  visibilityListeners.add(cb)
  return () => {
    visibilityListeners.delete(cb)
  }
}

export const isGleapReady = (): boolean =>
  gleapInitialized && gleapInstance != null && !gleapForceHidden

/**
 * Returns the loaded Gleap SDK reference for callers (e.g. the analytics
 * provider) that need to make calls. Returns null when Gleap has not
 * finished initializing yet — callers should treat that as a no-op.
 */
export const getGleap = (): GleapModule | null => gleapInstance

export const shouldInitGleap = (pathname?: string): boolean => {
  const path =
    pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname)
  return !EXTERNAL_SIGNER_ROUTE_PATTERNS.some((pattern) => pattern.test(path))
}

/**
 * The support chat follows `hasSupportChatDisabled`, and which account answers
 * depends on whether anyone is logged in yet:
 *
 * - logged in — the user's own account decides, on any domain, white-label or not;
 * - pre-auth (login, register, forgot password, public badge) — there is no account
 *   context, so the tenant that owns the branded domain decides, via the public
 *   white-label endpoint. On app.signater.com that endpoint reports `false`, so
 *   Signater's own pages keep the chat.
 *
 * A branded domain is the exception to that second rule: pre-auth it never gets
 * the chat, whatever the domain owner chose. Its login screen is shared by the
 * reseller and every account it manages (see the origin scope's membership
 * check), so the domain owner's flag would decide for managed accounts that
 * opted out — and it is Signater's own bubble surfacing on a page the end
 * customer reads as the reseller's.
 *
 * Both call sites (this module's runtime evaluation and `GleapBootstrap`) go
 * through here so the rule cannot drift between them.
 */
export const isSupportChatDisabled = (
  accountFlag: boolean | undefined,
  brandedDomainFlag: boolean,
  isBrandedDomain: boolean,
): boolean => accountFlag ?? (isBrandedDomain || brandedDomainFlag)

const isGleapDisabledByAccount = (): boolean =>
  isSupportChatDisabled(
    useContextStore.getState().userAccountContext?.accountInformation
      .hasSupportChatDisabled,
    useWhiteLabelStore.getState().hasSupportChatDisabled,
    useWhiteLabelStore.getState().isWhiteLabel,
  )

export const setGleapVisible = (visible: boolean): void => {
  if (!gleapInitialized || !gleapInstance) return
  try {
    gleapInstance.showFeedbackButton(visible)
    setGleapBubbleVisible(visible)
  } catch (error) {
    logger.error('Gleap showFeedbackButton failed', { visible, error })
  }
}

// The "Help" entry in the sidebar is the sole caller. It is rendered only
// when `hasSupportChatDisabled` is false (see nav-secondary), so we never
// want to fall back to opening an external site — that would surprise users
// who explicitly clicked "Help" inside the app. If Gleap somehow isn't ready,
// we log and no-op.
export const openGleap = (): void => {
  if (!isGleapReady() || !gleapInstance) {
    logger.error('openGleap called while Gleap is not ready')
    return
  }
  try {
    gleapInstance.open()
  } catch (error) {
    logger.error('Gleap open failed', { error })
  }
}

const applyGleapLanguage = (locale: Locale): void => {
  if (!gleapInstance) return
  try {
    gleapInstance.setLanguage(LOCALE_TO_BCP47[locale])
  } catch (error) {
    logger.error('Gleap setLanguage failed', { locale, error })
  }
}

/**
 * Neutral bubble that inverts with the app theme (black on light, white on
 * dark): the launcher is a utility, and the brand yellow stays reserved for
 * in-app actions. setStyles overrides the Gleap dashboard styling and takes
 * all four colours at once, so the widget panel follows the inversion too.
 */
type GleapThemeStyles = Parameters<(typeof Gleap)['setStyles']>[0]

const GLEAP_THEME_STYLES: Record<'light' | 'dark', GleapThemeStyles> = {
  light: {
    primaryColor: '#18181b',
    headerColor: '#18181b',
    buttonColor: '#18181b',
    backgroundColor: '#ffffff',
  },
  dark: {
    primaryColor: '#fafafa',
    headerColor: '#fafafa',
    buttonColor: '#fafafa',
    backgroundColor: '#1b1b1f',
  },
}

// The 16.0.8 runtime takes positional arguments — the object signature in the
// package's own index.d.ts belongs to a newer SDK and throws at runtime here.
// Re-check on the next gleap upgrade.
type PositionalSetStyles = (
  primaryColor: string,
  headerColor: string,
  buttonColor: string,
  backgroundColor?: string,
) => void

const applyGleapTheme = (theme: 'light' | 'dark'): void => {
  if (!gleapInstance) return
  const styles = GLEAP_THEME_STYLES[theme]
  try {
    ;(gleapInstance.setStyles as unknown as PositionalSetStyles)(
      styles.primaryColor,
      styles.headerColor,
      styles.buttonColor,
      styles.backgroundColor,
    )
  } catch (error) {
    logger.error('Gleap setStyles failed', { theme, error })
  }
}

/**
 * Loads the Gleap SDK chunk on demand and initializes it. Returns a
 * promise so callers (notably `GleapBootstrap`) can chain
 * `setupGleapVisibility` after the SDK is in memory; otherwise the first
 * visibility evaluation runs before `gleapInitialized` is true and is a
 * silent no-op.
 */
export const initGleap = async (): Promise<void> => {
  const sdkKey = import.meta.env.VITE_GLEAP_SDK_KEY

  if (!sdkKey) {
    logger.error('Gleap SDK key not found in environment variables')
    return
  }

  try {
    // Dynamic import — the rollup chunk for `gleap` only downloads here.
    const mod = await import('gleap')
    gleapInstance = mod.default

    gleapInstance.initialize(sdkKey)
    gleapInitialized = true
    gleapForceHidden = isGleapDisabledByAccount()

    applyGleapLanguage(useLanguageStore.getState().locale)
    useLanguageStore.subscribe((state, prev) => {
      if (state.locale !== prev.locale) {
        applyGleapLanguage(state.locale)
      }
    })

    applyGleapTheme(useThemeStore.getState().theme)
    // The dashboard's remote widget config lands after initialize() and
    // overwrites styles applied too early — re-assert ours once it is in.
    gleapInstance.on('initialized', () => {
      applyGleapTheme(useThemeStore.getState().theme)
    })
    useThemeStore.subscribe((state, prev) => {
      if (state.theme !== prev.theme) {
        applyGleapTheme(state.theme)
      }
    })

    logger.info('Gleap initialized successfully', {
      forceHidden: gleapForceHidden,
    })
  } catch (error) {
    logger.error('Failed to initialize Gleap', { error })
  }
}

const isDesktopViewport = (): boolean => {
  if (typeof window === 'undefined') return true
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches
}

export const suppressGleap = (): (() => void) => {
  gleapSuppressions += 1
  reevaluateGleapVisibility?.()
  let released = false
  return () => {
    if (released) return
    released = true
    gleapSuppressions -= 1
    reevaluateGleapVisibility?.()
  }
}

export const setupGleapVisibility = (router: AnyRouter): void => {
  const evaluate = (): void => {
    if (!gleapInitialized) return
    const pathname = router.state.location.pathname
    const shouldShow =
      !gleapForceHidden &&
      gleapSuppressions === 0 &&
      shouldInitGleap(pathname) &&
      isDesktopViewport()
    setGleapVisible(shouldShow)
  }
  reevaluateGleapVisibility = evaluate

  evaluate()
  router.subscribe('onResolved', evaluate)

  if (typeof window !== 'undefined') {
    window.matchMedia(DESKTOP_MEDIA_QUERY).addEventListener('change', evaluate)
  }

  useContextStore.subscribe(() => {
    gleapForceHidden = isGleapDisabledByAccount()
    evaluate()
  })

  // Logout clears the account context and resets this store to Signater defaults
  // before the public settings are refetched, so the branded domain's answer only
  // arrives on a later update — without this subscription the bubble would linger
  // on a tenant login page that opted out.
  useWhiteLabelStore.subscribe(() => {
    gleapForceHidden = isGleapDisabledByAccount()
    evaluate()
  })
}
